import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { getTokens, removeTokens, saveTokens } from "shared/context/auth-context";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { toast } from "react-toastify";
import _ from "lodash";

const apiVersion = "/api/v1";
const baseURL = process.env.REACT_APP_BASE_URL;
export const fileURL = process.env.REACT_APP_FILE_URL + apiVersion;
const clientInstant = axios.create({ baseURL });

export interface PagedResponse<TData> {
  data: TData[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

export interface Response<T = any> {
  errors: ResponseError[];
  isSuccess: boolean;
  data: T;
}
export interface ResponseError {
  fieldName: string;
  code: string;
  message: string;
}

clientInstant.interceptors.request.use((config) => {
  const { token } = getTokens();
  if (token) {
    config.headers!.authorization = `Bearer ${token}`;
  }
  return config;
});

export async function handleRefreshToken(call: any) {
  const { token, refreshToken, expirationDate } = getTokens();

  try {
    const { data: response } = await axios.post(baseURL + "/Owner/refreshToken", { token, refreshToken });

    if (response.isSuccess) {
      saveTokens(
        response.data.token,
        response.data.refreshToken,
        expirationDate ? new Date(expirationDate) : undefined
      );
      call.response.config.headers["authorization"] = "Bearer " + response.data.token;
      return Promise.resolve();
    } else {
      removeTokens();
      window.location.reload();
      return Promise.reject();
    }
  } catch (e) {
    removeTokens();
    window.location.reload();
    return Promise.reject();
  }
}

createAuthRefreshInterceptor(clientInstant, handleRefreshToken,{
  statusCodes:[401]
});

export const mapServerErrors = (response: Response) => {
  let errors: any = { others: [] };

  (response.errors ?? []).forEach((error) => {
    // if (fieldName) {
    //   errors[_.camelCase(fieldName)] = error.code;
    // } else {
    //   errors.others.push(error.message);
    // }
    errors.others.push(error.message);
  });

  if (_.isEmpty(errors.others)) {
    delete errors.others;
    toast.error("Server Error");
  } else errors.others.forEach((err: string) => toast.error(err));
  return errors;
};



export function getServerErrorMessage(errors: any[]) {
  const mapped: string[] = [];
  errors.forEach((error) => mapped.push(error.description));
  return mapped.join(", ");
}

export function serverImage(imageId?: string) {
  if (!imageId) return;
  return fileURL + "/files/previewFile?id=" + imageId;
}


/**
 function to convert loadOptions to PagedListParams
 * @param filters FilterDescriptor[]
 * @returns string
 */
export function mapFilters(filters?: any[]) {
  if (!filters) return;

  const filter: any = {};

  if (typeof filters[0] === "string") {
    filter[filters[0]] = { operator: getOperator(filters[1]), value: filters[2] };
  } else {
    filters.forEach((item) => {
      if (Array.isArray(item)) {
        Object.assign(filter, mapFilters(item));
      }
    });
  }

  return filter;
}

function getOperator(operator: string) {
  switch (operator) {
    case "=":
      return "equals";
    case "<>":
      return "notEquals";
    case ">":
      return "GreaterThan";
    case ">=":
      return "GreaterThanOrEquals";
    case "<":
      return "LessThan";
    case "<=":
      return "LessThanOrEquals";
    default:
      return operator;
  }
}



/**
 * Downloads a binary file (PDF, Excel…) from the API and triggers a browser
 * download. Uses clientInstant so the Bearer token is included automatically.
 */
export async function downloadPdf(endpoint: string, filename: string): Promise<void> {
  const response = await clientInstant.get(endpoint, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Downloads any binary file from the API and triggers a browser download,
 * keeping the content type the server reported. Uses clientInstant so the
 * Bearer token is included automatically.
 */
export async function downloadFile(
  endpoint: string,
  filename: string,
  configs: AxiosRequestConfig = {}
): Promise<void> {
  const response = await clientInstant.get(endpoint, { ...configs, responseType: "blob" });
  const type = response.headers["content-type"] || "application/octet-stream";
  const url = URL.createObjectURL(new Blob([response.data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function client<T>(endpoint: string, { data, ...configs }: AxiosRequestConfig = {}) {
  return clientInstant({
    data,
    url: endpoint,
    method: data ? "POST" : "GET",
    ...configs,
  })
    .then((axiosResponse: AxiosResponse<Response<T>>) =>  axiosResponse.data)
    .then((response) => {
      if (response.isSuccess) {
        return { response: response.data, error: null };
      } else {
        const errors = mapServerErrors(response);
        return { response: null, error: errors };
      }
    })
    .catch((err) => {
      return { response: null, error: err };
    });
}
